import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { BbField } from '@/app/pages/create-brief-select/ui/bb-field/bb-field';
import { BbEditList } from '@/app/pages/create-brief-select/ui/bb-edit-list/bb-edit-list';
import { BbAiThoughtsScroll } from '@/app/pages/create-brief-select/ui/bb-ai-thoughts-scroll/bb-ai-thoughts-scroll';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { ScreeningBriefAiService } from '../../services/screening-brief-ai.service';
import { AddDocumentComponent } from '@/app/atoms/add-document/add-document.component';
import { WysiswygComponent } from '@/app/atoms/wysiswyg/wysiswyg.component';
import { MatCalendarCellClassFunction, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { htmlListToLines, linesToHtmlList } from '../../screening-brief.utils';

type CreativeMethod = 'upload' | 'type' | 'template' | 'ai';

@Component({
  selector: 'sbs-brief-requirements',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbAiThoughtsScroll, AddDocumentComponent, WysiswygComponent, BbField, BbEditList, NgTemplateOutlet, MatDatepickerModule, MatFormFieldModule, MatInputModule],
  templateUrl: './brief-requirements.html',
  styleUrl: './brief-requirements.scss',
})
export class BriefRequirements {

  /** When true (wizard flow only), show the four-way method chooser above the editor. */
  readonly showMethodChooser = input<boolean>(false);

  readonly createScreeningService = inject(CreateScreeningService);
  readonly screeningAi = inject(ScreeningBriefAiService);

  /** Optional free-text steer for the Help Me AI draft. */
  readonly prompt = signal('');

  /** Editable list rows, mirrored to/from the service HTML signals (contentRequirements / contentDonts). */
  readonly requirements = signal<string[]>([]);
  readonly donts = signal<string[]>([]);

  readonly examples: { name: string; description: string; prompt: string }[] = [
    { name: 'On-brand advocates', description: 'Creators already posting on-brand content.', prompt: 'Find creators whose past content already aligns with our brand tone and product category.' },
    { name: 'Authentic users', description: 'Genuine past mentions of the product.', prompt: 'Screen for creators who have authentically featured or mentioned this product (or its category) in previous posts.' },
    { name: 'Hands-on demos', description: 'Real product demos in past content.', prompt: 'Look for past content showing hands-on demos of similar products, with no paid competitor promotions.' },
    { name: 'Family-friendly', description: 'Consistently safe, on-brand tone.', prompt: 'Screen for family-friendly creators with a consistent, brand-safe tone across their content history.' },
  ];

  readonly overviewPlaceholder =
    'e.g. We want creators who already talk about haircare in a natural, everyday way — not polished brand ads. Look for genuine product use in the last year.';
  readonly overviewHint =
    'What a strong match looks like in a creator’s existing posts. Do not ask for new videos, captions, or deliverables.';
  readonly requirementPlaceholder = 'e.g. Has posted about this product category in the last 12 months';
  readonly redFlagPlaceholder = 'e.g. Paid posts for a competing brand';
  readonly lookbackHint = 'SHOUT checks posts from this date until today. You can go back up to 3 years.';

  /** The HTML we last wrote to the service, so the resync effect ignores our own edits. */
  private lastRequirementsHtml = '';
  private lastDontsHtml = '';

  constructor() {
    this.requirements.set(htmlListToLines(this.createScreeningService.requirementsHtml()));
    this.donts.set(htmlListToLines(this.createScreeningService.restrictionsHtml()));
    if (this.requirements().length === 0) {
      this.requirements.set(['']);
    }
    if (this.donts().length === 0) {
      this.donts.set(['']);
    }
    this.lastRequirementsHtml = this.createScreeningService.requirementsHtml();
    this.lastDontsHtml = this.createScreeningService.restrictionsHtml();

    // Re-hydrate the lists when the AI (re)drafts and replaces the HTML externally.
    effect(() => {
      const requirementsHtml = this.createScreeningService.requirementsHtml();
      const dontsHtml = this.createScreeningService.restrictionsHtml();
      if (requirementsHtml !== this.lastRequirementsHtml) {
        this.lastRequirementsHtml = requirementsHtml;
        const nextRequirements = htmlListToLines(requirementsHtml);
        this.requirements.set(nextRequirements.length ? nextRequirements : ['']);
      }
      if (dontsHtml !== this.lastDontsHtml) {
        this.lastDontsHtml = dontsHtml;
        const nextDonts = htmlListToLines(dontsHtml);
        this.donts.set(nextDonts.length ? nextDonts : ['']);
      }
    });
  }

  /** The four creative-direction input modes rendered by the chooser. */
  readonly methods: { id: CreativeMethod; title: string; desc: string; tag?: string }[] = [
    { id: 'upload', title: 'Upload Document', desc: 'Bring existing screening rules — PDF, DOCX or PPTX.' },
    { id: 'type', title: 'Type Requirements', desc: 'Write what past content must show, and what should fail.' },
    { id: 'ai', title: 'Help Me', desc: 'Let SHOUT AI draft screening criteria from your brand.', tag: 'AI' },
  ];

  today: Date = new Date();
  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month') {
      const date = cellDate.getDate();
    }
    return '';
  };

  /** Switch the active input mode; opening the template drawer immediately for the template mode. */
  selectMethod(id: CreativeMethod): void {
    this.createScreeningService.creativeMethod.set(id);
  }

  /** Help Me mode: stream a screening-brief draft, then land in the Type editor on success. */
  async draftWithAi(): Promise<void> {
    if (this.screeningAi.isDrafting()) {
      return;
    }
    const angle =
      this.prompt().trim() ||
      'Draft a strong content-screening brief considering this product and brand.';
    await this.screeningAi.draftScreeningBrief(angle);
  }

  onDocumentUploaded(event: { file: string, fileKey: string, fullUrl: string }) {
    this.createScreeningService.requirementsDocumentUrl.set(event.fullUrl);
  }

  onHtmlContentChange(event: string, type: 'briefText' | 'requirements' | 'restrictions') {
    if (type === 'briefText') {
      this.createScreeningService.briefTextHtml.set(event);
    } else if (type === 'requirements') {
      this.createScreeningService.requirementsHtml.set(event);
    } else {
      this.createScreeningService.restrictionsHtml.set(event);
    }
  }

  /** Update the "What content must show" rows; store back as an HTML list (contentRequirements). */
  setRequirements(items: string[]): void {
    this.requirements.set(items);
    this.lastRequirementsHtml = linesToHtmlList(items);
    this.createScreeningService.requirementsHtml.set(this.lastRequirementsHtml);
  }

  /** Update the "Red flags & disqualifiers" rows; store back as an HTML list (contentDonts). */
  setDonts(items: string[]): void {
    this.donts.set(items);
    this.lastDontsHtml = linesToHtmlList(items);
    this.createScreeningService.restrictionsHtml.set(this.lastDontsHtml);
  }


  onDateChange(event: any) {
    this.createScreeningService.screenContentUntil.set(event);
  }

  readonly checkIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            class="size-4 text-green-500 dark:text-green-400">
            <path d="M20 6 9 17l-5-5" />
        </svg>`;

  readonly crossIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
  class="size-4 text-red-500 dark:text-red-400">
  <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
  </svg>`
}
